import {DehydratedState, Hydrate, onlineManager, QueryClient, QueryClientProvider} from '@tanstack/react-query';
import {ReactQueryDevtools} from '@tanstack/react-query-devtools';
import {useServerConfig} from 'api/queries/config/useServerConfig';
import axios from 'axios';
import {API_HOST, isDev} from 'config';
import {useRouter} from 'next/router';
import {FC, PropsWithChildren, useEffect, useState} from 'react';

import {useCartStore} from '../../store/cartStore';
import {AddressProvider} from './AddressProvider';
import {CardPaymentProvider} from './CardPaymentProvider';
import {CartProvider} from './CartProvider';
import {CustomerProvider} from './CustomerProvider';
import {OrderProvider} from './OrderProvider';
import {ThemeProvider} from './theme-provider';

interface Props {
  dehydratedState?: DehydratedState;
  fallback?: {
    [key: string]: any;
  };
}

const ServerConfigConsumer: FC<PropsWithChildren> = ({children}) => {
  const {locale} = useRouter();
  const [header, setHeader] = useState<string>('');
  const {setColaPromo} = useCartStore();
  const {data: serverConfig} = useServerConfig();

  useEffect(() => {
    const fetchMaintenanceContent = async () => {
      try {
        const response = await axios.get(`${API_HOST}/${locale || 'ru'}/maintenance`);
        setHeader(response.data);
      } catch (error) {
        console.error('Error fetching maintenance content:', error);
      }
    };
    if (serverConfig) {
      setColaPromo({
        cola_promo_active: serverConfig.cola_promo_active,
        cola_promo_threshold: serverConfig.cola_promo_threshold,
      });
      if (serverConfig.maintenance_mode) {
        fetchMaintenanceContent();
      }
    }
  }, [serverConfig, locale, setColaPromo]);

  if (header) {
    return (
      <div
        style={{
          zIndex: 9999,
        }}
      >
        <html
          style={{width: '100vw', height: '100vh', zIndex: 9999}}
          dangerouslySetInnerHTML={{
            __html: `<style>
    html, body {
      margin: 0;
      width: 100vw;
      height: 100vh;
    }
  </style>${header}`,
          }}
        />
      </div>
    );
  }

  return <>{children}</>;
};

const Providers: FC<PropsWithChildren<Props>> = ({children, ...pageProps}) => {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: false,
            retryDelay: 5000,
            refetchOnWindowFocus: false,
            staleTime: 2 * 60 * 1000,
            //this fix the issue when client open straus and requests are not made, because of network
            // @see - https://tanstack.com/query/v4/docs/framework/react/guides/network-mode
            networkMode: 'always',
          },
          mutations: {
            //this ensures mutations also work regardless of network detection
            networkMode: 'always',
          },
        },
      }),
  );
  /**
   *main.js?ts=1720082170629:511 Warning: Expected server HTML to contain a matching <div> in <div>.
    at div
    at eval (
   */
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);

    const handleOnline = () => {
      onlineManager.setOnline(true);
    };

    const handleOffline = () => {
      onlineManager.setOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (!isMounted) return null;

  return (
    <QueryClientProvider client={queryClient}>
      <Hydrate state={pageProps.dehydratedState}>
        <ServerConfigConsumer>
          <ThemeProvider>
            <CustomerProvider>
              <AddressProvider>
                <CardPaymentProvider>
                  <OrderProvider>
                    <CartProvider>{children}</CartProvider>
                  </OrderProvider>
                </CardPaymentProvider>
              </AddressProvider>
            </CustomerProvider>
          </ThemeProvider>
        </ServerConfigConsumer>
      </Hydrate>
      {isDev && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
};

export default Providers;
